import React, { useEffect, useRef, useState } from 'react';
import PdfModal from './PdfModal';

interface Props {
  html: string;
}

/**
 * Renderuje HTML popis produktu a vylepšuje ho:
 *  - PDF odkazy (fotonávody) převede na tlačítka otevírající modal náhled
 *  - po sobě jdoucí obrázky (technické nákresy) dá vedle sebe
 *  - uklidí prázdné odstavce, placeholdery ("Bude doplněn") a prázdné sekce
 */
const FazonetkaDescription: React.FC<Props> = ({ html }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [pdf, setPdf] = useState<{ url: string; title: string } | null>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    try {

    const norm = (s: string) => s.replace(/ /g, ' ').trim();
    const isPlaceholder = (t: string) =>
      t === '' || /^(bude dopln|p[řr]ipravuj|p[řr]ipravuje se|dopln[íi]me|bude p[řr]id)/i.test(t);

    // 0a) odstranit Shoptet smetí připletené do popisu (rating widget, prázdné galerie)
    root.querySelectorAll('.rate-wrapper, .rate-wrap, .rate-average-wrap, .plus-gallery-wrap')
      .forEach((el) => el.remove());
    // <meta> a jiné nesmysly z editoru
    root.querySelectorAll('meta, link, style, script').forEach((el) => el.remove());
    // WordPress/block-editor smetí v atributech + pevné rozměry tabulek
    root.querySelectorAll('*').forEach((el) => {
      ['role', 'aria-multiline', 'aria-label', 'tabindex', 'contenteditable',
       'data-block', 'data-type', 'data-title', 'data-rich-text-format-boundary', 'id'].forEach((a) => el.removeAttribute(a));
      const cls = el.getAttribute('class');
      if (cls && /block-editor|wp-block|rich-text|is-multi|has-inline-color|has-.*-color|has-background/.test(cls)) el.removeAttribute('class');
      if (el.tagName === 'TABLE') { el.removeAttribute('width'); el.removeAttribute('height'); }
    });

    // 0b) zploštit vnořené layout <div> obaly (page-builder) -> ploché sekce,
    //     aby dělení do sloupců/karet fungovalo stejně jako u ploché struktury
    let guard = 0;
    let d: Element | null;
    while (guard++ < 4000 && (d = root.querySelector('div'))) {
      const parent = d.parentNode!;
      while (d.firstChild) parent.insertBefore(d.firstChild, d);
      parent.removeChild(d);
    }
    // 0c) volné text/inline uzly zabalit do <p> (rekonstrukce odstavců)
    const INLINE = new Set(['SPAN', 'STRONG', 'B', 'I', 'EM', 'A', 'BR', 'SMALL', 'U', 'FONT']);
    let run: ChildNode[] = [];
    const flushRun = () => {
      if (!run.length) return;
      const p = document.createElement('p');
      run[0].parentNode!.insertBefore(p, run[0]);
      run.forEach((x) => p.appendChild(x));
      run = [];
    };
    Array.from(root.childNodes).forEach((n) => {
      const isText = n.nodeType === 3 && (n.textContent || '').trim() !== '';
      const isInline = n.nodeType === 1 && INLINE.has((n as Element).tagName);
      if (isText || isInline) run.push(n);
      else { flushRun(); if (n.nodeType === 3) (n as ChildNode).remove(); }
    });
    flushRun();

    // 0) nechtěné sekce podle nadpisu pryč (i s obsahem)
    const dropSection = /jak slepit/i;
    root.querySelectorAll('h2,h3').forEach((h) => {
      if (!dropSection.test(h.textContent || '')) return;
      let n = h.nextElementSibling;
      while (n && n.tagName !== 'H2' && n.tagName !== 'H3') { const x = n.nextElementSibling; n.remove(); n = x; }
      h.remove();
    });

    // 1) prázdné a placeholder odstavce ("Bude doplněn", "Připravujeme"…)
    root.querySelectorAll('p').forEach((p) => {
      if (p.querySelector('img,a,iframe')) return;
      if (isPlaceholder(norm(p.textContent || ''))) p.remove();
    });

    // 2) PDF odkazy -> tlačítka
    root.querySelectorAll<HTMLAnchorElement>('a[href$=".pdf"], a[href*=".pdf?"]').forEach((a) => {
      const img = a.querySelector('img');
      let label = '';
      const wrap = a.closest('p');
      const prev = wrap?.previousElementSibling as HTMLElement | null;
      if (prev && prev.tagName === 'P' && !prev.querySelector('a,img') && prev.textContent!.trim()) {
        label = prev.textContent!.trim();
        prev.remove();
      } else if (img?.alt && !/ikona|icon/i.test(img.alt)) {
        label = img.alt.trim();
      }
      if (!label) label = 'Fotonávod (PDF)';

      a.classList.add('fz-navod-btn');
      a.setAttribute('data-pdf-title', label);
      a.innerHTML = `<span class="fz-navod-btn__ico" aria-hidden="true"></span><span class="fz-navod-btn__label">${label}</span>`;
      if (wrap && wrap.parentNode) {
        wrap.parentNode.insertBefore(a, wrap);
        if (!wrap.querySelector('a,img') && wrap.textContent!.trim() === '') wrap.remove();
      }
    });

    // 3a) odstavec s VÍCE obrázky (oba nákresy v jednom <p>) -> rovnou řada
    root.querySelectorAll('p,figure').forEach((el) => {
      const imgs = el.querySelectorAll('img');
      if (imgs.length >= 2 && !el.textContent!.trim()) el.classList.add('fz-figrow');
    });

    // 3b) po sobě jdoucí samostatné obrázky vedle sebe
    const imgOf = (el: Element | null): HTMLImageElement | null => {
      if (!el) return null;
      if (el.tagName === 'IMG') return el as HTMLImageElement;
      if ((el.tagName === 'P' || el.tagName === 'FIGURE') && el.children.length === 1 &&
          el.firstElementChild!.tagName === 'IMG' && !el.textContent!.trim()) {
        return el.firstElementChild as HTMLImageElement;
      }
      return null;
    };
    let i = 0;
    const kids = Array.from(root.children);
    while (i < kids.length) {
      if (imgOf(kids[i])) {
        let j = i;
        while (j < kids.length && imgOf(kids[j])) j++;
        if (j - i >= 2) {
          const row = document.createElement('div');
          row.className = 'fz-figrow';
          root.insertBefore(row, kids[i]);
          for (let k = i; k < j; k++) { row.appendChild(imgOf(kids[k])!); kids[k].remove(); }
        }
        i = j;
      } else i++;
    }

    // 3c) karty po variantách — <p><strong>label</strong></p> uvozuje kartu
    const isCardTitle = (el: Element | null): el is HTMLParagraphElement =>
      !!el && el.tagName === 'P' && el.children.length === 1 &&
      el.firstElementChild!.tagName === 'STRONG' &&
      el.textContent!.trim() === el.firstElementChild!.textContent!.trim() &&
      el.textContent!.trim() !== '';
    // jen přímí potomci root (ploché sekce) — u vnořených struktur karty neděláme
    Array.from(root.children).filter(isCardTitle).forEach((p) => {
      const parent = p.parentNode;
      if (!parent || (parent as HTMLElement).classList?.contains('fz-card')) return;
      const title = p.textContent!.trim().replace(/[:：]\s*$/, '');
      const card = document.createElement('div');
      card.className = 'fz-card';
      const h = document.createElement('div');
      h.className = 'fz-card__title';
      h.textContent = title;
      card.appendChild(h);
      parent.insertBefore(card, p);
      p.remove();
      let n = card.nextElementSibling;
      while (n && !isCardTitle(n) && n.tagName !== 'H2' && n.tagName !== 'H3' &&
             !(n as HTMLElement).classList.contains('fz-card')) {
        const next = n.nextElementSibling;
        card.appendChild(n);
        n = next;
      }
    });

    // 4) prázdné sekce (h3 bez obsahu, např. zbylý "Fotonávod")
    const meaningful = (el: Element) =>
      el.querySelector('img,iframe,a.fz-navod-btn,.fz-figrow') !== null || el.textContent!.trim() !== '';
    root.querySelectorAll('h2,h3').forEach((h) => {
      const drop: Element[] = [];
      let n = h.nextElementSibling;
      while (n && n.tagName !== 'H2' && n.tagName !== 'H3') {
        if (meaningful(n)) return;
        drop.push(n);
        n = n.nextElementSibling;
      }
      drop.forEach((d) => d.remove());
      h.remove();
    });

    // 5) rozložení: text vlevo, vizuál (nákres/obrázky) vpravo, TABULKY plná šířka dole
    type Grp = { head: Element | null; nodes: Element[] };
    const isHead = (el: Element) => el.tagName === 'H2' || el.tagName === 'H3' || el.tagName === 'H4';
    const groups: Grp[] = [];
    let cur: Grp | null = null;
    Array.from(root.children).forEach((el) => {
      if (isHead(el)) { cur = { head: el, nodes: [el] }; groups.push(cur); }
      else { if (!cur) { cur = { head: null, nodes: [] }; groups.push(cur); } cur.nodes.push(el); }
    });
    const hasTable = (g: Grp) => g.nodes.some((n) => n.tagName === 'TABLE' || n.querySelector('table') !== null);
    const visualHead = /n[áa]kres|technick/i;
    const isVisual = (g: Grp) => {
      if (g.head && visualHead.test(g.head.textContent || '')) return true;
      const hasVis = g.nodes.some((n) =>
        n.tagName === 'IMG' || n.classList.contains('fz-figrow') || n.querySelector('img,iframe') !== null);
      if (!hasVis) return false;
      const txt = g.nodes.filter((n) => n !== g.head).map((n) => n.textContent || '').join(' ').trim();
      return txt.length <= 220;
    };
    const main = document.createElement('div'); main.className = 'fz-desc__main';
    const side = document.createElement('div'); side.className = 'fz-desc__side';
    const full = document.createElement('div'); full.className = 'fz-desc__full';
    groups.forEach((g) => {
      const tgt = hasTable(g) ? full : (isVisual(g) ? side : main);
      g.nodes.forEach((n) => tgt.appendChild(n));
    });
    if (side.childElementCount > 0) {
      const cols = document.createElement('div'); cols.className = 'fz-desc__cols';
      cols.appendChild(main); cols.appendChild(side);
      root.appendChild(cols);
    } else {
      root.appendChild(main);
    }
    if (full.childElementCount > 0) root.appendChild(full);

    } catch (err) {
      // jakákoli neočekávaná struktura popisu nesmí shodit detail — necháme syrový HTML
      // eslint-disable-next-line no-console
      console.warn('FazonetkaDescription: transform skipped', err);
    }
  }, [html]);

  // delegovaný klik – otevři modal místo navigace na PDF
  const onClick = (e: React.MouseEvent) => {
    const a = (e.target as HTMLElement).closest<HTMLAnchorElement>('a.fz-navod-btn');
    if (!a) return;
    e.preventDefault();
    setPdf({ url: a.getAttribute('href') || '', title: a.getAttribute('data-pdf-title') || 'Fotonávod' });
  };

  return (
    <>
      <div
        ref={ref}
        className="pd-section__body pd-tabs__description fz-desc"
        onClick={onClick}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {pdf && <PdfModal url={pdf.url} title={pdf.title} onClose={() => setPdf(null)} />}
    </>
  );
};

export default FazonetkaDescription;
